// ============================================================================
// PLAYER
// ============================================================================

import CONFIG from '../config.js';
import { lerp, easeOutElastic } from '../utils/helpers.js';

// Input will be imported as a global (handled by main.js)
// We'll access it via window.Input or import it if we convert Input to ES6 module

export class Player {
    constructor(x, y, physics) {
        this.x = x;
        this.y = y;
        this.physics = physics;
        
        // Create Matter.js body for player (circle) - DYNAMIC
        // We apply forces to move, physics handles collision response
        if (physics) {
            // Import collision categories
            const { CATEGORY, MASK } = physics.constructor;
            
            this.body = physics.createCircle(x, y, CONFIG.playerSize / 2, {
                isStatic: false,      // Dynamic body
                frictionAir: 0.15,    // Damping to prevent sliding
                friction: 0.1,
                restitution: 0,       // No bounce
                mass: 5,              // Moderate mass
                inertia: Infinity,    // Prevent rotation
                collisionFilter: {
                    category: CATEGORY.PLAYER,
                    mask: MASK.PLAYER  // Collides with walls, furniture, doors (NOT throwables)
                }
            });
            physics.linkEntity(this, this.body);
        } else {
            this.body = null;
        }
        
        // Movement force multiplier (tune this for feel)
        this.moveForce = 0.009; // Increased for better responsiveness
        this.targetX = null;
        this.targetY = null;
        this.animTime = 0;
        this.lastX = x;
        this.lastY = y;
        this.idleTime = 0;

        // Run mechanic
        this.isRunning = false;  // True when running toward target (double-click), false when walking (single-click)

        // Pickup & Carry system
        this.carryingObject = null;  // Reference to object being carried (DetachedDoor or other pickable)
        this.pickupTime = 0;          // Time when object was picked up (for delay before charging)
        this.isChargingThrow = false; // Whether currently charging a throw
        this.throwChargeStartTime = 0; // Time when throw charging started (for UI bar)

        // Aggression system (shared for cupboard interaction and throw charging)
        this.aggression = 0;  // Aggression level (0 to aggressiveMax) - used for both cupboard and throw

        // Emotion/Anger system
        this.anger = 0;              // Current anger level (0-100)
        this.targetColor = CONFIG.playerColor;
        this.currentColor = CONFIG.playerColor;
        this.isPunching = false;
        this.punchTime = 0;
        this.punchAngle = 0;

        // Rage action system (random actions when angry)
        this.rageActionTimer = 0;
        this.rageActionInterval = 5 + Math.random() * 5; // 5-10 seconds
        this.currentRageAction = null;
        this.rageActionTime = 0;
        
        // Rage action types
        this.rageActions = [
            { type: 'jumpPunch', interruptsMovement: true, duration: 3.0, name: 'Jump Punch' }, // 3 seconds
            { type: 'speechBubble', interruptsMovement: false, duration: 1.5, name: 'Speech Bubble' },
            { type: 'stomp', interruptsMovement: true, duration: 0.4, name: 'Stomp' },
            { type: 'rageShake', interruptsMovement: false, duration: 0.8, name: 'Rage Shake' }
        ];
        
        // Speech bubble expletives
        this.expletives = ['you suck', 'damn it', 'argh!'];
        this.currentSpeechBubble = null;
        
        // Direction tracking (for eyes and throws)
        this.lastDirection = { x: 0, y: -1 }; // Default: facing up
    }

    update(dt, tilemap, game) {
        // Store last position for animation
        this.lastX = this.x;
        this.lastY = this.y;
        
        // Sync position FROM physics body (dynamic body - physics handles movement)
        if (this.body) {
            this.x = this.body.position.x;
            this.y = this.body.position.y;
        }

        this.animTime += dt;

        // Update anger (decays over time) - only if anger system is enabled
        if (CONFIG.angerEnabled) {
            if (this.anger > 0) {
                this.anger -= CONFIG.angerDecayRate * dt;
                this.anger = Math.max(0, this.anger);
            }

            // Update color based on anger using the color progression system
            // Color scheme: Green → Yellow → Orange → Red → Dark Red/Purple → Ultra-dark Purple
            this.targetColor = this.getColorForAnger(this.anger);

            // Smooth color transition
            this.currentColor = this.lerpColor(this.currentColor, this.targetColor, CONFIG.angerColorTransitionSpeed * dt);
        } else {
            // Keep player at default color when anger is disabled
            this.targetColor = CONFIG.playerColor;
            this.currentColor = CONFIG.playerColor;
            this.anger = 0;
        }

        // Update punch animation
        if (this.isPunching) {
            this.punchTime += dt;
            if (this.punchTime >= CONFIG.punchDuration) {
                this.isPunching = false;
                this.punchTime = 0;
            }
        }

        // Check if actively interacting (holding E/Space/right-click on an interactive object)
        // Movement is blocked during interaction with BreakableEntity
        const isInteracting = (game.cupboard && game.cupboard.isHolding) ||
                             (game.wardrobe && game.wardrobe.isHolding) ||
                             (game.chest && game.chest.isHolding) ||
                             (game.alarmClock && game.alarmClock.isHolding) ||
                             (game.trees && game.trees.some(tree => tree.isHolding)) ||
                             (game.doors && game.doors.some(door => door.isHolding));
        
        // Don't allow movement if:
        // 1. Interacting with BreakableEntity (cupboard, wardrobe, chest, alarm clock, trees, doors)
        // 2. Carrying object and charging throw (loading aggression)
        const shouldBlockMovement = isInteracting || (this.carryingObject && this.isChargingThrow);
        
        if (shouldBlockMovement) {
            // Clear movement targets and stop movement
            this.targetX = null;
            this.targetY = null;
            this.isRunning = false;
            return; // Exit early, no movement
        }

        // Update rage action system (only when angry/not green) - only if anger system is enabled
        const isHappy = CONFIG.angerEnabled ? (this.anger < CONFIG.angerColorStages[1].min) : true; // Below "Irritated" threshold
        if (CONFIG.angerEnabled && !isHappy && !isInteracting) {
            // Update timer
            this.rageActionTimer += dt;
            
            // Check if it's time for a new rage action
            if (this.rageActionTimer >= this.rageActionInterval && !this.currentRageAction) {
                // Pick a random rage action
                const randomAction = this.rageActions[Math.floor(Math.random() * this.rageActions.length)];
                this.currentRageAction = randomAction;
                this.rageActionTime = 0;
                
                // Initialize action-specific data
                if (randomAction.type === 'speechBubble') {
                    this.currentSpeechBubble = this.expletives[Math.floor(Math.random() * this.expletives.length)];
                }
                
                // Reset timer for next action
                this.rageActionTimer = 0;
                this.rageActionInterval = 5 + Math.random() * 5; // 5-10 seconds
            }
            
            // Update current rage action
            if (this.currentRageAction) {
                this.rageActionTime += dt;
                
                // Check if action is complete
                if (this.rageActionTime >= this.currentRageAction.duration) {
                    this.currentRageAction = null;
                    this.rageActionTime = 0;
                    this.currentSpeechBubble = null;
                }
            }
        } else {
            // Reset timer if happy or interacting
            this.rageActionTimer = 0;
            this.currentRageAction = null;
            this.rageActionTime = 0;
            this.currentSpeechBubble = null;
        }

        let dx = 0;
        let dy = 0;
        
        // Block movement if current rage action interrupts movement
        if (this.currentRageAction && this.currentRageAction.interruptsMovement) {
            // Movement is blocked, skip all movement calculations
            this.targetX = null;
            this.targetY = null;
            this.isRunning = false;
            return; // Exit early, no movement
        }

        // Keyboard movement (always walking, no run for keyboard)
        if (Input.isDown('arrowright') || Input.isDown('d')) dx = 1;
        else if (Input.isDown('arrowleft') || Input.isDown('a')) dx = -1;

        if (Input.isDown('arrowdown') || Input.isDown('s')) dy = 1;
        else if (Input.isDown('arrowup') || Input.isDown('w')) dy = -1;
        
        // Update last direction if there's input (keep last direction when not moving)
        if (dx !== 0 || dy !== 0) {
            this.lastDirection.x = dx;
            this.lastDirection.y = dy;
        }

        // Check for double-key press to trigger run (only if anger is enabled)
        const doubleKey = Input.consumeDoubleKeyPress();
        if (doubleKey) {
            let runX = 0, runY = 0;
            if (doubleKey === 'arrowright' || doubleKey === 'd') runX = 1;
            else if (doubleKey === 'arrowleft' || doubleKey === 'a') runX = -1;
            if (doubleKey === 'arrowdown' || doubleKey === 's') runY = 1;
            else if (doubleKey === 'arrowup' || doubleKey === 'w') runY = -1;

            if (runX !== 0 || runY !== 0) {
                // Normalize diagonal
                if (runX !== 0 && runY !== 0) {
                    runX *= 0.707;
                    runY *= 0.707;
                }
                // Set target far ahead in the run direction
                this.targetX = this.x + runX * 1000;
                this.targetY = this.y + runY * 1000;
                // Only enable running if anger system is enabled
                this.isRunning = CONFIG.angerEnabled;
            }
        }

        // Double-click for run (only if anger is enabled)
        if (Input.consumeDoubleClick()) {
            // Set target (walk or run depending on anger enabled)
            this.targetX = Input.mouseWorldX;
            this.targetY = Input.mouseWorldY;
            // Only enable running if anger system is enabled
            this.isRunning = CONFIG.angerEnabled;
            
            // Update last direction based on movement target
            if (this.targetX !== null && this.targetY !== null) {
                const targetDx = this.targetX - this.x;
                const targetDy = this.targetY - this.y;
                const targetDist = Math.sqrt(targetDx * targetDx + targetDy * targetDy);
                if (targetDist > 0.1) {
                    this.lastDirection.x = targetDx / targetDist;
                    this.lastDirection.y = targetDy / targetDist;
                }
            }
        }

        // Left-click for movement (single click = walk)
        if (Input.consumeLeftClick()) {
            this.targetX = Input.mouseWorldX;
            this.targetY = Input.mouseWorldY;
            this.isRunning = false; // Single click = walk
            
            // Update last direction based on movement target
            if (this.targetX !== null && this.targetY !== null) {
                const targetDx = this.targetX - this.x;
                const targetDy = this.targetY - this.y;
                const targetDist = Math.sqrt(targetDx * targetDx + targetDy * targetDy);
                if (targetDist > 0.1) {
                    this.lastDirection.x = targetDx / targetDist;
                    this.lastDirection.y = targetDy / targetDist;
                }
            }
        }

        // Move toward click target if no keyboard input
        if (dx === 0 && dy === 0 && this.targetX !== null && this.targetY !== null) {
            const diffX = this.targetX - this.x;
            const diffY = this.targetY - this.y;
            const dist = Math.sqrt(diffX * diffX + diffY * diffY);

            if (dist > 5) {
                dx = diffX / dist;
                dy = diffY / dist;
                // Update last direction while moving toward target
                this.lastDirection.x = dx;
                this.lastDirection.y = dy;
            } else {
                // Reached target
                this.targetX = null;
                this.targetY = null;
                this.isRunning = false;
            }
        }

        // Clear target and run state if using keyboard
        if ((Input.isDown('arrowright') || Input.isDown('d') ||
             Input.isDown('arrowleft') || Input.isDown('a') ||
             Input.isDown('arrowdown') || Input.isDown('s') ||
             Input.isDown('arrowup') || Input.isDown('w'))) {
            this.targetX = null;
            this.targetY = null;
            this.isRunning = false; // Keyboard movement is always walking
        }

        // Normalize diagonal movement
        if (dx !== 0 && dy !== 0) {
            dx *= 0.707;
            dy *= 0.707;
        }

        // Dynamic body movement - apply forces, physics handles collision
        if (this.body && this.physics && (dx !== 0 || dy !== 0)) {
            // Calculate force based on whether running or walking
            const forceMultiplier = this.isRunning ? this.moveForce * 2 : this.moveForce;
            
            // Apply force to the physics body
            this.physics.applyForce(this.body, dx * forceMultiplier, dy * forceMultiplier);
        }
        
        // Check for wall hits during run for anger system - only if anger system is enabled
        if (CONFIG.angerEnabled && this.isRunning && this.targetX !== null && this.targetY !== null) {
            // Check if target is blocked by a wall (for anger system)
            const toTargetX = this.targetX - this.x;
            const toTargetY = this.targetY - this.y;
            const toTargetDist = Math.sqrt(toTargetX * toTargetX + toTargetY * toTargetY);
            
            if (toTargetDist > 0) {
                const dirToTargetX = toTargetX / toTargetDist;
                const dirToTargetY = toTargetY / toTargetDist;
                
                // Check if target direction is blocked by a wall
                const checkDist = CONFIG.playerSize * 2;
                const checkX = this.x + dirToTargetX * checkDist;
                const checkY = this.y + dirToTargetY * checkDist;
                
                // If the target direction is blocked by a wall, trigger anger
                if (!tilemap.isWalkable(checkX, checkY, CONFIG.playerSize)) {
                    // Hit obstacle during run with target beyond obstacle - get angry!
                    this.onWallHitDuringRun(game);
                }
            }
        }

        // Update carried object position
        if (this.carryingObject) {
            this.carryingObject.updateCarriedPosition(this.x, this.y);
        }

        // Throw charge is updated in Game.update() to access debugMode

        // Track idle time
        const isMoving = Math.abs(this.x - this.lastX) > 0.1 || Math.abs(this.y - this.lastY) > 0.1;
        if (isMoving) {
            this.idleTime = 0;
        } else {
            this.idleTime += dt;
        }
    }

    throwObject(targetX, targetY, game = null) {
        if (!this.carryingObject) return;

        // Calculate throw force based on aggression level (same scale as spawn uses damage)
        // Cap aggression to prevent tunneling at extreme values
        const throwForce = Math.min(this.aggression, CONFIG.throwableMaxAggression);
        
        // Track last interaction aggression for debug display
        if (game) {
            game.lastInteractionAggression = this.aggression;
        }

        // Throw the object
        this.carryingObject.throwAt(targetX, targetY, throwForce);
        this.carryingObject = null;
        
        // Reset aggression after throwing
        this.aggression = 0;
        this.isChargingThrow = false;
        this.pickupTime = 0;
    }

    startChargingThrow() {
        if (this.carryingObject && !this.isChargingThrow) {
            const currentTime = performance.now() / 1000; // Current time in seconds
            const timeSincePickup = currentTime - this.pickupTime;
            
            // Only start charging if enough time has passed since pickup
            if (timeSincePickup >= CONFIG.throwChargeDelay) {
                this.isChargingThrow = true;
                this.aggression = 0; // Reset aggression when starting to charge
                this.throwChargeStartTime = currentTime; // Store start time in seconds
            }
        }
    }

    updateAggression(dt, debugMode = false) {
        // Update aggression for throw charging (cupboard handles its own via updateHold)
        // This method is called for throw charging, cupboard uses its own updateHold method
        if (this.isChargingThrow && this.carryingObject) {
            // In debug mode, accumulate aggression faster for testing
            const multiplier = debugMode ? CONFIG.debugAggressionMultiplier : 1;
            this.aggression = Math.min(this.aggression + dt * CONFIG.aggressionRate * multiplier, CONFIG.maxConMaxAggression);
        }
    }

    stopChargingThrow() {
        this.isChargingThrow = false;
        this.throwChargeStartTime = 0;
        this.aggression = 0; // Reset aggression when stopping
    }
    
    /**
     * Draws UI elements when charging throw (aggression bar above the player).
     */
    drawUI(ctx, camera) {
        // Only show aggression bar when charging throw
        if (!this.isChargingThrow || !this.carryingObject) {
            return;
        }
        
        // Get player position (from physics body if available, otherwise use stored position)
        let playerX = this.x;
        let playerY = this.y;
        
        // If player has a physics body, use its position (more accurate)
        if (this.body && this.body.position) {
            playerX = this.body.position.x;
            playerY = this.body.position.y;
        }
        
        // NOTE: Camera transform is already applied in game.js before calling drawUI
        // So we use world coordinates directly - they will be transformed by the existing camera transform
        
        // Position bar above the player (10px above)
        const barWidth = 60;
        const barHeight = 10;
        const radiusY = CONFIG.playerSize * 1.2; // Vertical radius of player oval
        
        // Calculate bar position in world coordinates (camera transform already applied)
        const barX = playerX - barWidth / 2;
        // Position 10px above the top of the player
        const barY = playerY - radiusY - barHeight - 10; // 10px above the top
        const cornerRadius = 3; // For rounded corners
        
        const maxAggression = CONFIG.maxConMaxAggression;
        const currentAggression = Math.max(0, Math.min(this.aggression || 0, maxAggression));
        const fillPercent = Math.max(0, Math.min(1, currentAggression / maxAggression)); // Clamp 0-1
        
        // Determine bar color based on aggression level
        // For throw charging, we don't have a baseline time - just show aggression level
        let barColor;
        let barBgColor; // Lighter/transparent version for background
        const slamHighThreshold = maxAggression * 0.5; // 50% of max (50 aggression points)
        
        // Color logic:
        // - Green: low aggression (below 25% of max)
        // - Yellow: medium aggression (25% to 50% of max)
        // - Red: high aggression (above 50% of max)
        if (currentAggression < maxAggression * 0.25) {
            // Green: low aggression
            barColor = '#4CAF50';
            barBgColor = 'rgba(76, 175, 80, 0.3)'; // Lighter/transparent green
        } else if (currentAggression < slamHighThreshold) {
            // Yellow: medium aggression (25% to 50%)
            barColor = '#FFEB3B';
            barBgColor = 'rgba(255, 235, 59, 0.3)'; // Lighter/transparent yellow
        } else {
            // Red: high aggression (above 50%)
            barColor = '#F44336';
            barBgColor = 'rgba(244, 67, 54, 0.3)'; // Lighter/transparent red
        }
        
        // Helper function to draw rounded rectangle
        const drawRoundedRect = (x, y, width, height, radius) => {
            ctx.beginPath();
            ctx.moveTo(x + radius, y);
            ctx.lineTo(x + width - radius, y);
            ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
            ctx.lineTo(x + width, y + height - radius);
            ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
            ctx.lineTo(x + radius, y + height);
            ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
            ctx.lineTo(x, y + radius);
            ctx.quadraticCurveTo(x, y, x + radius, y);
            ctx.closePath();
        };
        
        // Draw background fill (full width, lighter/transparent version of current color)
        ctx.fillStyle = barBgColor;
        drawRoundedRect(barX, barY, barWidth, barHeight, cornerRadius);
        ctx.fill();
        
        // Draw progress fill (actual width based on aggression, full opacity)
        const fillWidth = barWidth * fillPercent;
        if (fillWidth > 0) {
            ctx.fillStyle = barColor;
            // Draw fill with actual width (no minimum clamp) so bar starts at 0% and fills smoothly
            drawRoundedRect(barX, barY, fillWidth, barHeight, cornerRadius);
            ctx.fill();
        }
        
        // Always draw border (black, rounded rectangle) so bar is visible even at 0 aggression
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2; // Slightly thicker for visibility
        drawRoundedRect(barX, barY, barWidth, barHeight, cornerRadius);
        ctx.stroke();
        
        // NOTE: Don't restore camera transform here - it's restored in game.js after all UI is drawn
    }

    pickupObject(object) {
        if (this.carryingObject) return false; // Already carrying something
        if (object.pickup(this)) {
            this.carryingObject = object;
            this.pickupTime = performance.now() / 1000; // Record pickup time
            this.isChargingThrow = false; // Reset charging state
            this.aggression = 0; // Reset aggression
            return true;
        }
        return false;
    }

    dropObject() {
        if (this.carryingObject) {
            this.carryingObject.isCarried = false;
            this.carryingObject = null;
            this.pickupTime = 0;
            this.isChargingThrow = false;
            this.aggression = 0;
        }
    }

    onWallHitDuringRun(game) {
        // Increase anger
        this.anger = Math.min(100, this.anger + CONFIG.angerFromWallHit);

        // Trigger punch animation
        this.isPunching = true;
        this.punchTime = 0;
        this.punchAngle = Math.random() * Math.PI * 2; // Random punch direction

        // Camera shake
        game.camera.shake(3, 0.2);

        // Stop running when hitting wall
        this.isRunning = false;
        this.targetX = null;
        this.targetY = null;
    }

    getColorForAnger(angerLevel) {
        // Get color based on anger level using the color progression system
        // Color scheme: Green → Yellow → Orange → Red → Dark Red/Purple → Ultra-dark Purple
        const stages = CONFIG.angerColorStages;
        
        // Clamp anger to valid range
        const clampedAnger = Math.max(0, Math.min(100, angerLevel));
        
        // Find the current stage and next stage
        let currentStage = stages[0];
        let nextStage = stages[1];
        
        for (let i = 0; i < stages.length; i++) {
            if (clampedAnger >= stages[i].min && clampedAnger <= stages[i].max) {
                currentStage = stages[i];
                // If not the last stage, get the next one for interpolation
                if (i < stages.length - 1) {
                    nextStage = stages[i + 1];
                } else {
                    // Last stage, use itself for interpolation (no transition)
                    nextStage = stages[i];
                }
                break;
            }
        }
        
        // If we're at the exact color of a stage, return it directly
        if (currentStage === nextStage || clampedAnger === currentStage.max) {
            return currentStage.color;
        }
        
        // Interpolate between current and next stage colors
        const stageRange = currentStage.max - currentStage.min;
        const positionInStage = clampedAnger - currentStage.min;
        const t = positionInStage / stageRange; // 0 to 1 within the stage
        
        return this.lerpColorHex(currentStage.color, nextStage.color, t);
    }

    lerpColorHex(color1Hex, color2Hex, t) {
        // Helper to lerp between two hex colors
        const parseHex = (hex) => {
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            return { r, g, b };
        };
        
        const c1 = parseHex(color1Hex);
        const c2 = parseHex(color2Hex);
        
        const r = Math.floor(lerp(c1.r, c2.r, t));
        const g = Math.floor(lerp(c1.g, c2.g, t));
        const b = Math.floor(lerp(c1.b, c2.b, t));
        
        return `rgb(${r}, ${g}, ${b})`;
    }

    lerpColor(color1, color2, t) {
        // Simple color lerp (works with hex or rgb)
        // For simplicity, we'll just return target if close enough
        if (Math.abs(t) >= 0.9) return color2;

        // Parse RGB values
        const parseColor = (c) => {
            if (c.startsWith('#')) {
                return {
                    r: parseInt(c.slice(1, 3), 16),
                    g: parseInt(c.slice(3, 5), 16),
                    b: parseInt(c.slice(5, 7), 16)
                };
            } else if (c.startsWith('rgb')) {
                const match = c.match(/\d+/g);
                return { r: parseInt(match[0]), g: parseInt(match[1]), b: parseInt(match[2]) };
            }
            return { r: 76, g: 175, b: 80 }; // Default green (calm state)
        };

        const c1 = parseColor(color1);
        const c2 = parseColor(color2);

        const r = Math.floor(lerp(c1.r, c2.r, t));
        const g = Math.floor(lerp(c1.g, c2.g, t));
        const b = Math.floor(lerp(c1.b, c2.b, t));

        return `rgb(${r}, ${g}, ${b})`;
    }

    draw(ctx, camera) {
        ctx.save();
        
        // Player size
        const radius = CONFIG.playerSize;
        const radiusX = radius; // Horizontal radius
        const radiusY = radius * 1.2; // Vertical radius (slightly taller for oval)

        // 1. SHADOW (On the floor)
        ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
        ctx.beginPath();
        ctx.ellipse(this.x, this.y, radiusX, radiusX * 0.6, 0, 0, Math.PI * 2);
        ctx.fill();

        // 2. ANIMATION TRANSFORMS
        const time = this.animTime;
        const isMoving = Math.abs(this.x - this.lastX) > 0.1 || Math.abs(this.y - this.lastY) > 0.1;
        
        let scaleX = 1;
        let scaleY = 1;
        let jumpY = 0;

        if (this.isRunning && isMoving) {
            scaleX = 1.2; scaleY = 0.8;
        } else if (isMoving) {
            const bounce = Math.abs(Math.sin(time * 10)) * 3;
            jumpY = -bounce;
            scaleX = 1 + Math.sin(time * 10) * 0.05;
        }

        // Apply Rage Action transforms
        if (this.currentRageAction && this.currentRageAction.type === 'jumpPunch') {
            const progress = this.rageActionTime / this.currentRageAction.duration;
            const jumpHeight = Math.sin(progress * Math.PI) * 50;
            jumpY -= jumpHeight;
            scaleX *= (1 + Math.sin(progress * Math.PI) * 0.5);
            scaleY *= (1 + Math.sin(progress * Math.PI) * 0.5);
        }

        ctx.translate(this.x, this.y + jumpY);
        ctx.scale(scaleX, scaleY);

        // 3. BODY (Single circle/oval)
        ctx.globalAlpha = 1.0;
        ctx.fillStyle = this.currentColor;
        ctx.beginPath();
        ctx.ellipse(0, 0, radiusX, radiusY, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.stroke();

        // 4. EYES (Near the top of the circle/oval) - use last direction
        ctx.fillStyle = '#000';
        const eyeY = -radiusY * 0.3; // Position eyes near the top (30% from center toward top)
        const eyeX = this.lastDirection.x * 2; // Slight offset based on last facing direction
        const eyeSpacing = radiusX * 0.3; // Space between eyes
        const eyeSize = radiusX * 0.15; // Eye size
        
        ctx.beginPath();
        ctx.arc(eyeX - eyeSpacing / 2, eyeY + this.lastDirection.y * 1, eyeSize, 0, Math.PI * 2);
        ctx.arc(eyeX + eyeSpacing / 2, eyeY + this.lastDirection.y * 1, eyeSize, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
        
        // Draw speech bubble and punch effects in world space
        if (this.currentRageAction && this.currentRageAction.type === 'speechBubble' && this.currentSpeechBubble) {
            this.drawSpeechBubble(ctx, camera);
        }

        if (this.isPunching) {
            const punchProgress = this.punchTime / CONFIG.punchDuration;
            const punchDistance = easeOutElastic(punchProgress) * 30;
            ctx.save();
            ctx.translate(this.x, this.y - radiusY);
            ctx.globalAlpha = 1 - punchProgress;
            ctx.fillStyle = '#FF0000';
            ctx.beginPath(); ctx.arc(0, 0, punchDistance, 0, Math.PI * 2); ctx.fill();
            ctx.restore();
        }
    }

    drawSpeechBubble(ctx, camera) {
        // Draw speech bubble above player (camera is already applied in main draw loop)
        const bubbleX = this.x;
        const bubbleY = this.y - CONFIG.playerSize - 40;
        const bubbleWidth = this.currentSpeechBubble.length * 8 + 20;
        const bubbleHeight = 25;
        
        // Draw bubble background
        ctx.fillStyle = '#fff';
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        
        // Rounded rectangle for bubble (using manual drawing)
        const radius = 5;
        ctx.beginPath();
        ctx.moveTo(bubbleX - bubbleWidth / 2 + radius, bubbleY - bubbleHeight / 2);
        ctx.lineTo(bubbleX + bubbleWidth / 2 - radius, bubbleY - bubbleHeight / 2);
        ctx.quadraticCurveTo(bubbleX + bubbleWidth / 2, bubbleY - bubbleHeight / 2, bubbleX + bubbleWidth / 2, bubbleY - bubbleHeight / 2 + radius);
        ctx.lineTo(bubbleX + bubbleWidth / 2, bubbleY + bubbleHeight / 2 - radius);
        ctx.quadraticCurveTo(bubbleX + bubbleWidth / 2, bubbleY + bubbleHeight / 2, bubbleX + bubbleWidth / 2 - radius, bubbleY + bubbleHeight / 2);
        ctx.lineTo(bubbleX - bubbleWidth / 2 + radius, bubbleY + bubbleHeight / 2);
        ctx.quadraticCurveTo(bubbleX - bubbleWidth / 2, bubbleY + bubbleHeight / 2, bubbleX - bubbleWidth / 2, bubbleY + bubbleHeight / 2 - radius);
        ctx.lineTo(bubbleX - bubbleWidth / 2, bubbleY - bubbleHeight / 2 + radius);
        ctx.quadraticCurveTo(bubbleX - bubbleWidth / 2, bubbleY - bubbleHeight / 2, bubbleX - bubbleWidth / 2 + radius, bubbleY - bubbleHeight / 2);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        
        // Draw tail pointing to player
        ctx.beginPath();
        ctx.moveTo(bubbleX - 10, bubbleY + bubbleHeight / 2);
        ctx.lineTo(bubbleX, bubbleY + bubbleHeight / 2 + 10);
        ctx.lineTo(bubbleX + 10, bubbleY + bubbleHeight / 2);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        
        // Draw text
        ctx.fillStyle = '#000';
        ctx.font = 'bold 14px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(this.currentSpeechBubble, bubbleX, bubbleY);
    }
    
    // Helper: Check circle (player) vs axis-aligned rectangle collision
    checkRectCircleCollision(circleX, circleY, circleRadius, rect) {
        // Find the closest point on the rectangle to the circle center
        const closestX = Math.max(rect.x - rect.width / 2, Math.min(circleX, rect.x + rect.width / 2));
        const closestY = Math.max(rect.y - rect.height / 2, Math.min(circleY, rect.y + rect.height / 2));
        
        // Calculate distance from circle center to closest point
        const dx = circleX - closestX;
        const dy = circleY - closestY;
        const distanceSquared = dx * dx + dy * dy;
        
        // Check if distance is less than circle radius
        return distanceSquared < (circleRadius * circleRadius);
    }
    
    // Helper: Check circle vs rotated rectangle collision
    checkRotatedRectCircleCollision(circleX, circleY, circleRadius, rectX, rectY, rectWidth, rectHeight, angleRad) {
        // Transform circle center to rectangle's local coordinate system
        const cos = Math.cos(-angleRad);
        const sin = Math.sin(-angleRad);
        const dx = circleX - rectX;
        const dy = circleY - rectY;
        const localX = dx * cos - dy * sin;
        const localY = dx * sin + dy * cos;
        
        // Now do standard AABB vs Circle check in local space
        const closestX = Math.max(-rectWidth / 2, Math.min(localX, rectWidth / 2));
        const closestY = Math.max(-rectHeight / 2, Math.min(localY, rectHeight / 2));
        
        // Calculate distance from circle center to closest point
        const distX = localX - closestX;
        const distY = localY - closestY;
        const distanceSquared = distX * distX + distY * distY;
        
        // Check if distance is less than circle radius
        return distanceSquared < (circleRadius * circleRadius);
    }
}
